/**
 * Point samplers for the shape generator. All shapes are centered on the
 * origin in editor world space (y-up): plane and disc lie in the xz plane
 * with their normal along +y.
 *
 * Three distributions are supported:
 * - 'grid':   jittered grid (surfaces/volumes) or Fibonacci spiral (sphere
 *             hull) - visually even coverage without a visible pattern, the
 *             default
 * - 'exact':  the same grid without jitter - a perfectly regular raster
 * - 'random': uniform random - more organic, tends to clump
 *
 * Internally the grid samplers take `gridJitter`: null selects random
 * sampling, otherwise the value is the jitter amplitude as a fraction of the
 * cell size (0 = exact).
 */

import { estimatePointShape, samplePointShape } from './point-shapes';
import { createRng, createGaussianRng, Rng } from './rng';
import { SampleSet, ShapeParams } from './types';

const GRID_JITTER = 0.4;  // default jitter amplitude for 'grid' distribution

class SampleWriter {
    positions: number[] = [];
    normals: number[] = [];
    hasNormals: boolean;

    constructor(hasNormals: boolean) {
        this.hasNormals = hasNormals;
    }

    push(x: number, y: number, z: number, nx = 0, ny = 1, nz = 0) {
        this.positions.push(x, y, z);
        if (this.hasNormals) {
            this.normals.push(nx, ny, nz);
        }
    }

    result(spacing: number): SampleSet {
        return {
            count: this.positions.length / 3,
            positions: new Float32Array(this.positions),
            normals: this.hasNormals ? new Float32Array(this.normals) : null,
            spacing
        };
    }
}

// sample a rectangle spanned by (uAxis * uSize) x (vAxis * vSize) around
// `origin` with the given constant normal
const sampleRect = (
    writer: SampleWriter, rng: Rng, gridJitter: number | null, density: number,
    origin: [number, number, number],
    uAxis: [number, number, number], uSize: number,
    vAxis: [number, number, number], vSize: number,
    normal: [number, number, number],
    offsetRange: number
) => {
    const place = (u: number, v: number) => {
        // hull thickness: offset along the normal
        const o = offsetRange > 0 ? (rng() - 0.5) * offsetRange : 0;
        writer.push(
            origin[0] + uAxis[0] * u + vAxis[0] * v + normal[0] * o,
            origin[1] + uAxis[1] * u + vAxis[1] * v + normal[1] * o,
            origin[2] + uAxis[2] * u + vAxis[2] * v + normal[2] * o,
            normal[0], normal[1], normal[2]
        );
    };

    if (gridJitter !== null) {
        const cell = 1 / Math.sqrt(density);
        const nu = Math.max(1, Math.round(uSize / cell));
        const nv = Math.max(1, Math.round(vSize / cell));
        const du = uSize / nu;
        const dv = vSize / nv;
        for (let i = 0; i < nu; i++) {
            for (let j = 0; j < nv; j++) {
                place(
                    -uSize / 2 + (i + 0.5 + (rng() - 0.5) * 2 * gridJitter) * du,
                    -vSize / 2 + (j + 0.5 + (rng() - 0.5) * 2 * gridJitter) * dv
                );
            }
        }
    } else {
        const n = Math.max(1, Math.ceil(density * uSize * vSize));
        for (let i = 0; i < n; i++) {
            place((rng() - 0.5) * uSize, (rng() - 0.5) * vSize);
        }
    }
};

const samplePlane = (writer: SampleWriter, rng: Rng, gridJitter: number | null, density: number, width: number, depth: number) => {
    sampleRect(writer, rng, gridJitter, density, [0, 0, 0], [1, 0, 0], width, [0, 0, 1], depth, [0, 1, 0], 0);
};

const sampleDisc = (writer: SampleWriter, rng: Rng, gridJitter: number | null, density: number, radius: number) => {
    if (gridJitter !== null) {
        // grid over the bounding square, clipped to the circle
        const cell = 1 / Math.sqrt(density);
        const n = Math.max(1, Math.round((2 * radius) / cell));
        const d = (2 * radius) / n;
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
                const x = -radius + (i + 0.5 + (rng() - 0.5) * 2 * gridJitter) * d;
                const z = -radius + (j + 0.5 + (rng() - 0.5) * 2 * gridJitter) * d;
                if (x * x + z * z <= radius * radius) {
                    writer.push(x, 0, z);
                }
            }
        }
    } else {
        const n = Math.max(1, Math.ceil(density * Math.PI * radius * radius));
        for (let i = 0; i < n; i++) {
            // sqrt for uniform area distribution
            const r = radius * Math.sqrt(rng());
            const theta = 2 * Math.PI * rng();
            writer.push(r * Math.cos(theta), 0, r * Math.sin(theta));
        }
    }
};

const sampleCubeHull = (
    writer: SampleWriter, rng: Rng, gridJitter: number | null, density: number,
    width: number, height: number, depth: number, thickness: number
) => {
    const w2 = width / 2;
    const h2 = height / 2;
    const d2 = depth / 2;

    type Face = {
        origin: [number, number, number];
        uAxis: [number, number, number]; uSize: number;
        vAxis: [number, number, number]; vSize: number;
        normal: [number, number, number];
    };

    const faces: Face[] = [
        { origin: [w2, 0, 0], uAxis: [0, 0, 1], uSize: depth, vAxis: [0, 1, 0], vSize: height, normal: [1, 0, 0] },
        { origin: [-w2, 0, 0], uAxis: [0, 0, 1], uSize: depth, vAxis: [0, 1, 0], vSize: height, normal: [-1, 0, 0] },
        { origin: [0, h2, 0], uAxis: [1, 0, 0], uSize: width, vAxis: [0, 0, 1], vSize: depth, normal: [0, 1, 0] },
        { origin: [0, -h2, 0], uAxis: [1, 0, 0], uSize: width, vAxis: [0, 0, 1], vSize: depth, normal: [0, -1, 0] },
        { origin: [0, 0, d2], uAxis: [1, 0, 0], uSize: width, vAxis: [0, 1, 0], vSize: height, normal: [0, 0, 1] },
        { origin: [0, 0, -d2], uAxis: [1, 0, 0], uSize: width, vAxis: [0, 1, 0], vSize: height, normal: [0, 0, -1] }
    ];

    for (const face of faces) {
        sampleRect(writer, rng, gridJitter, density, face.origin, face.uAxis, face.uSize, face.vAxis, face.vSize, face.normal, thickness);
    }
};

const sampleCubeFilled = (
    writer: SampleWriter, rng: Rng, gridJitter: number | null, density: number,
    width: number, height: number, depth: number
) => {
    if (gridJitter !== null) {
        const cell = 1 / Math.cbrt(density);
        const nx = Math.max(1, Math.round(width / cell));
        const ny = Math.max(1, Math.round(height / cell));
        const nz = Math.max(1, Math.round(depth / cell));
        const dx = width / nx;
        const dy = height / ny;
        const dz = depth / nz;
        for (let i = 0; i < nx; i++) {
            for (let j = 0; j < ny; j++) {
                for (let k = 0; k < nz; k++) {
                    writer.push(
                        -width / 2 + (i + 0.5 + (rng() - 0.5) * 2 * gridJitter) * dx,
                        -height / 2 + (j + 0.5 + (rng() - 0.5) * 2 * gridJitter) * dy,
                        -depth / 2 + (k + 0.5 + (rng() - 0.5) * 2 * gridJitter) * dz
                    );
                }
            }
        }
    } else {
        const n = Math.max(1, Math.ceil(density * width * height * depth));
        for (let i = 0; i < n; i++) {
            writer.push((rng() - 0.5) * width, (rng() - 0.5) * height, (rng() - 0.5) * depth);
        }
    }
};

const sampleSphereHull = (
    writer: SampleWriter, rng: Rng, gridJitter: number | null, density: number,
    radius: number, thickness: number
) => {
    const n = Math.max(1, Math.ceil(density * 4 * Math.PI * radius * radius));

    const place = (nx: number, ny: number, nz: number) => {
        const r = radius + (thickness > 0 ? (rng() - 0.5) * thickness : 0);
        writer.push(nx * r, ny * r, nz * r, nx, ny, nz);
    };

    if (gridJitter !== null) {
        // Fibonacci sphere: near-even spacing without clustering at the
        // poles. inherently regular, so 'grid' and 'exact' are identical here
        const golden = Math.PI * (3 - Math.sqrt(5));
        for (let i = 0; i < n; i++) {
            const y = n === 1 ? 0 : 1 - (2 * i) / (n - 1);
            const r = Math.sqrt(Math.max(0, 1 - y * y));
            const theta = golden * i;
            place(Math.cos(theta) * r, y, Math.sin(theta) * r);
        }
    } else {
        // normalized gaussian vectors are uniform on the sphere
        const gauss = createGaussianRng(rng);
        for (let i = 0; i < n; i++) {
            let x, y, z, len;
            do {
                x = gauss();
                y = gauss();
                z = gauss();
                len = Math.sqrt(x * x + y * y + z * z);
            } while (len < 1e-8);
            place(x / len, y / len, z / len);
        }
    }
};

const sampleSphereFilled = (writer: SampleWriter, rng: Rng, gridJitter: number | null, density: number, radius: number) => {
    if (gridJitter !== null) {
        // 3d grid clipped to the sphere
        const cell = 1 / Math.cbrt(density);
        const n = Math.max(1, Math.round((2 * radius) / cell));
        const d = (2 * radius) / n;
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
                for (let k = 0; k < n; k++) {
                    const x = -radius + (i + 0.5 + (rng() - 0.5) * 2 * gridJitter) * d;
                    const y = -radius + (j + 0.5 + (rng() - 0.5) * 2 * gridJitter) * d;
                    const z = -radius + (k + 0.5 + (rng() - 0.5) * 2 * gridJitter) * d;
                    if (x * x + y * y + z * z <= radius * radius) {
                        writer.push(x, y, z);
                    }
                }
            }
        }
    } else {
        const n = Math.max(1, Math.ceil(density * (4 / 3) * Math.PI * radius * radius * radius));
        const gauss = createGaussianRng(rng);
        for (let i = 0; i < n; i++) {
            let x, y, z, len;
            do {
                x = gauss();
                y = gauss();
                z = gauss();
                len = Math.sqrt(x * x + y * y + z * z);
            } while (len < 1e-8);
            // cbrt for uniform volume distribution
            const r = radius * Math.cbrt(rng());
            writer.push((x / len) * r, (y / len) * r, (z / len) * r);
        }
    }
};

// expected splat count for the given parameters, without generating anything.
// used by the UI to display the count and enforce limits. grid distributions
// may deviate slightly (rounding, circle/sphere clipping).
const estimateCount = (params: ShapeParams): number => {
    const { kind, density } = params;
    const width = params.width ?? 1;
    const height = params.height ?? 1;
    const depth = params.depth ?? 1;
    const radius = params.radius ?? 0.5;

    switch (kind) {
        case 'plane':
            return Math.ceil(density * width * depth);
        case 'disc':
            return Math.ceil(density * Math.PI * radius * radius);
        case 'cube':
            return params.filled ?
                Math.ceil(density * width * height * depth) :
                Math.ceil(density * 2 * (width * depth + width * height + height * depth));
        case 'sphere':
            return params.filled ?
                Math.ceil(density * (4 / 3) * Math.PI * radius ** 3) :
                Math.ceil(density * 4 * Math.PI * radius * radius);
        case 'hull':
        case 'polygon':
            return estimatePointShape(kind, params.points, !!params.filled, density);
    }
};

const sampleShape = (params: ShapeParams): SampleSet => {
    const { kind, density } = params;
    const distribution = params.distribution ?? 'grid';
    const gridJitter = distribution === 'random' ? null : (distribution === 'exact' ? 0 : GRID_JITTER);
    const rng = createRng(params.seed ?? 42);
    const thickness = params.thickness ?? 0;

    const width = params.width ?? 1;
    const height = params.height ?? 1;
    const depth = params.depth ?? 1;
    const radius = params.radius ?? 0.5;

    const isVolume = !!params.filled && (kind === 'cube' || kind === 'sphere' || kind === 'hull');
    const isSurface = !isVolume;
    const spacing = isSurface ? 1 / Math.sqrt(density) : 1 / Math.cbrt(density);

    const writer = new SampleWriter(isSurface);

    switch (kind) {
        case 'plane':
            samplePlane(writer, rng, gridJitter, density, width, depth);
            break;
        case 'disc':
            sampleDisc(writer, rng, gridJitter, density, radius);
            break;
        case 'cube':
            if (params.filled) {
                sampleCubeFilled(writer, rng, gridJitter, density, width, height, depth);
            } else {
                sampleCubeHull(writer, rng, gridJitter, density, width, height, depth, thickness);
            }
            break;
        case 'sphere':
            if (params.filled) {
                sampleSphereFilled(writer, rng, gridJitter, density, radius);
            } else {
                sampleSphereHull(writer, rng, gridJitter, density, radius, thickness);
            }
            break;
        case 'hull':
        case 'polygon':
            samplePointShape(writer, rng, gridJitter, density, kind, params.points, !!params.filled, thickness);
            break;
    }

    return writer.result(spacing);
};

export { sampleShape, estimateCount };
